import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import { admin } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { getIntlLocale } from '../../i18n/runtime';
import {
  Users,
  MessageSquare,
  Link2,
  TrendingUp,
  Search,
  MoreHorizontal,
  Trash2,
  Shield,
  User,
  Loader2,
  LogIn,
} from 'lucide-react';

export default function Admin() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [impersonatingId, setImpersonatingId] = useState(null);

  const loadStats = useCallback(async () => {
    try {
      const data = await admin.getStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await admin.getUsers({ page });
      setUsers(data.users);
      setTotalPages(data.pages);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    loadStats();
    loadUsers();
  }, [loadStats, loadUsers]);

  const handleDelete = async (userId) => {
    if (!confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;

    try {
      await admin.deleteUser(userId);
      loadUsers();
      loadStats();
    } catch (err) {
      console.error('Failed to delete user:', err);
    }
  };

  const handleImpersonate = async (target) => {
    if (impersonatingId) return;
    setImpersonatingId(target.id);
    try {
      await admin.impersonate(target.id);
      await checkAuth();
      navigate('/app');
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err.message || 'Failed to enter user');
    } finally {
      setImpersonatingId(null);
    }
  };

  const statCards = [
    { label: 'Total Users', value: stats?.total_users || 0, icon: Users, color: 'bg-[#E6EFFF] text-[#0052CC]' },
    { label: 'Total Leads', value: stats?.total_leads || 0, icon: TrendingUp, color: 'bg-[#E6F4EA] text-[#1E7F4E]' },
    { label: 'Total Messages', value: stats?.total_messages || 0, icon: MessageSquare, color: 'bg-[#F0E6FF] text-[#6B00B3]' },
    { label: 'Connected Accounts', value: stats?.connected_accounts || 0, icon: Link2, color: 'bg-[#FFF4E5] text-[#B35900]' },
  ];

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString(getIntlLocale(), {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="app-page-shell bg-white">
      {/* Header */}
      <header className="h-14 border-b border-[#E3E2E0] flex items-center px-6 sticky top-0 bg-white z-10">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-[#6B6B6B]" />
          <h1 className="text-lg font-semibold text-[#191919]">Admin Dashboard</h1>
        </div>
        <nav className="ml-8 flex items-center gap-1 text-sm">
          <NavLink
            to="/admin"
            end
            className={({ isActive }) =>
              `px-3 py-1.5 rounded-lg ${isActive ? 'bg-[#F0F0EF] text-[#191919] font-medium' : 'text-[#6B6B6B] hover:bg-[#F7F7F5]'}`
            }
            data-testid="admin-tab-users"
          >
            Users
          </NavLink>
          <NavLink
            to="/admin/posts"
            className={({ isActive }) =>
              `px-3 py-1.5 rounded-lg ${isActive ? 'bg-[#F0F0EF] text-[#191919] font-medium' : 'text-[#6B6B6B] hover:bg-[#F7F7F5]'}`
            }
            data-testid="admin-tab-posts"
          >
            Posts
          </NavLink>
        </nav>
      </header>

      <div className="app-page-scroll">
        <div className="p-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat) => (
            <div key={stat.label} className="bg-white border border-[#E3E2E0] rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-[#6B6B6B]">{stat.label}</span>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stat.color}`}>
                  <stat.icon className="w-5 h-5" />
                </div>
              </div>
              <p className="text-3xl font-semibold text-[#191919]">
                {stat.value.toLocaleString(getIntlLocale())}
              </p>
            </div>
          ))}
        </div>

        {/* Users Table */}
        <div className="bg-white border border-[#E3E2E0] rounded-xl">
          <div className="p-4 border-b border-[#E3E2E0] flex items-center justify-between">
            <h2 className="text-base font-semibold text-[#191919]">Users</h2>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9B9B9B]" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search users..."
                className="w-full pl-10 pr-4 py-2 bg-[#F7F7F5] border border-[#E3E2E0] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0A0A0A] focus:border-transparent"
                data-testid="admin-search"
              />
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#6B6B6B]" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#F7F7F5]">
                  <tr>
                    <th className="text-left text-xs font-medium text-[#6B6B6B] uppercase px-4 py-3">User</th>
                    <th className="text-left text-xs font-medium text-[#6B6B6B] uppercase px-4 py-3">Joined</th>
                    <th className="text-left text-xs font-medium text-[#6B6B6B] uppercase px-4 py-3">LinkedIn</th>
                    <th className="text-left text-xs font-medium text-[#6B6B6B] uppercase px-4 py-3">OpenAI key</th>
                    <th className="text-right text-xs font-medium text-[#6B6B6B] uppercase px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E3E2E0]">
                  {users
                    .filter(u => 
                      !search || 
                      u.name?.toLowerCase().includes(search.toLowerCase()) ||
                      u.email?.toLowerCase().includes(search.toLowerCase())
                    )
                    .map((user) => (
                    <tr key={user.id} className="hover:bg-[#F7F7F5]" data-testid={`user-${user.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 bg-[#E3E2E0] rounded-full flex items-center justify-center">
                            <User className="w-4 h-4 text-[#6B6B6B]" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-[#191919]">{user.name}</p>
                            <p className="text-xs text-[#6B6B6B]">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-[#6B6B6B]">
                          {formatDate(user.created_at)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {user.linkedin_connected ? (
                          <span
                            className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md bg-[#E6F4EA] border border-[#C5E7D2] text-[#1E7F4E]"
                            data-testid={`linkedin-status-${user.id}`}
                          >
                            Connected
                          </span>
                        ) : (
                          <span
                            className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md bg-[#F0F0EF] border border-[#E3E2E0] text-[#6B6B6B]"
                            data-testid={`linkedin-status-${user.id}`}
                          >
                            Not connected
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <OpenAIKeyCell user={user} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          {user.role !== 'admin' && (
                            <button
                              onClick={() => handleImpersonate(user)}
                              disabled={impersonatingId === user.id}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-[#0052CC] hover:bg-[#E6EFFF] rounded-md transition-all disabled:opacity-60"
                              title={user.linkedin_connected ? 'Enter this user\u2019s account for support' : 'User has not connected LinkedIn yet, but you can still enter the account'}
                              data-testid={`impersonate-${user.id}`}
                            >
                              {impersonatingId === user.id ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <LogIn className="w-3.5 h-3.5" />
                              )}
                              Enter as user
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(user.id)}
                            className="p-2 hover:bg-[#FFEBE9] rounded-lg transition-all"
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-[#CE3636]" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-[#E3E2E0]">
              <span className="text-sm text-[#6B6B6B]">
                Page {page} of {totalPages}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1.5 text-sm border border-[#E3E2E0] rounded-lg hover:bg-[#F0F0EF] disabled:opacity-50 transition-all"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1.5 text-sm border border-[#E3E2E0] rounded-lg hover:bg-[#F0F0EF] disabled:opacity-50 transition-all"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}

function OpenAIKeyCell({ user }) {
  const [hasKey, setHasKey] = useState(null);
  const [editing, setEditing] = useState(false);
  const [keyValue, setKeyValue] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    admin.getUserOpenAIKeyStatus(user.id)
      .then((res) => active && setHasKey(res.has_key))
      .catch(() => active && setHasKey(false));
    return () => { active = false; };
  }, [user.id]);

  const save = async () => {
    setSaving(true);
    try {
      await admin.setUserOpenAIKey(user.id, keyValue);
      setHasKey(!!keyValue);
      setEditing(false);
      setKeyValue('');
    } finally {
      setSaving(false);
    }
  };

  const clearKey = async () => {
    if (!window.confirm('Remove this user\u2019s OpenAI key?')) return;
    setSaving(true);
    try {
      await admin.setUserOpenAIKey(user.id, '');
      setHasKey(false);
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1.5">
        <input
          type="password"
          autoFocus
          value={keyValue}
          onChange={(e) => setKeyValue(e.target.value)}
          placeholder="sk-..."
          className="px-2 py-1 border border-[#E3E2E0] rounded text-xs w-40"
          data-testid={`openai-key-input-${user.id}`}
        />
        <button onClick={save} disabled={saving || !keyValue} className="px-2 py-1 bg-[#0A0A0A] text-white text-xs rounded disabled:opacity-50" data-testid={`openai-save-${user.id}`}>
          {saving ? '…' : 'Save'}
        </button>
        <button onClick={() => { setEditing(false); setKeyValue(''); }} className="text-xs text-[#6B6B6B] hover:underline">Cancel</button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <span
        className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md border ${
          hasKey ? 'bg-[#E6F4EA] border-[#C5E7D2] text-[#1E7F4E]' : 'bg-[#F0F0EF] border-[#E3E2E0] text-[#6B6B6B]'
        }`}
        data-testid={`openai-status-${user.id}`}
      >
        {hasKey === null ? '…' : hasKey ? 'Set' : 'Not set'}
      </span>
      <button onClick={() => setEditing(true)} className="text-xs text-[#0052CC] hover:underline" data-testid={`openai-edit-${user.id}`}>
        {hasKey ? 'Update' : 'Add'}
      </button>
      {hasKey && (
        <button onClick={clearKey} className="text-xs text-[#CE3636] hover:underline" data-testid={`openai-clear-${user.id}`}>
          Remove
        </button>
      )}
    </div>
  );
}
